//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import * as _ from 'underscore';

import { ActivitySpinnerProps, ActivitySpinnerSize } from '@tableau/widgets';
import { getAppStore } from '../../app/AppStore';
import { RemoteAgentPresenceIcon } from '../../app/content/RemoteAgentPresenceIcon';
import { NavLocationSelectors } from '../../app/selectors/NavigationStateSelectors';
import { Messages } from '../../codegen/Localize';
import { SiteSettingSiteAdminTelemetryEvent } from '../../common/telemetryEvents/TelemetryEvents';
import { buildSiteSettingSiteAdminTelemetryEvent, getSiteSettingUUID } from '../../common/telemetryEvents/util/SiteSettingTelemetryUtil';
import { UserSettingsActions } from '../../userSettings/services/UserSettingsActions';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ByteSizeConversionFactor } from '../../commonInfra/utils/SizeFilter';
import { Module } from '../../ts/Module';
import { Sites } from '../../ts/siteManagement/services/resources/Sites';
import { IGetExtractEncryptionStatsResult } from '../../ts/ServerApi/requests/GetExtractEncryptionStatsRequest';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ExtractEncryptionService } from '../../extract/services/ExtractEncryptionService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { AskDataMode } from '../types/AskDataMode';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin, UserVisibilityMode } from '../types/ISiteSettingsForSiteAdmin';
import { HelpService } from '../../commonInfra/services/HelpService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { transformSubscriptionSettingsForApi, transformSubscriptionSettingsForScope } from '../directives/tbSiteSettingsForSiteAdminFormAdapter';
import { ExtractEncryptionMode } from '../../sites/SitePropertyTypes';

export interface IEncryptionStats {
  total: number;
  encrypted: number;
  unencrypted: number;
  encrypting: number;
  decrypting: number;
  inProgress: boolean;
}

export interface ISiteSettingsForSiteAdminFormScope extends ISettingsCoordinatorCtrlScope {
  siteSettingsForSiteAdminForm: ng.IFormController;
  remoteAgentPresenceIconProperties?: _.Dictionary<RemoteAgentPresenceIcon.Props>;
  siteRemoteAgentSettingsLearnMoreHref?: string;
  isSiteInviteNotificationUnavailable: boolean;
  extractEncryptionStats: IEncryptionStats;
  extractEncryptionStatusDescription: string;
  showStatus: boolean;
  extractEncryptionWarning: string;
  activitySpinnerProps: ActivitySpinnerProps;
  extractEncryptionStatusError: boolean;
  failureNotificationTitle: string;
  failureNotificationDetail: string;
  failureNotificationCheckboxLabel: string;
  showEARSetting: boolean;
  showSiteStartPageSetting: boolean;
  serverStartPage: string;  // Only shown to server admins
  siteStartPage: string;
  resetDefaultSiteStartPage: () => void;
  askDataDisabled: () => boolean;
  sandboxSiteStorageLimitRadioButtonLabel: string;
  sandboxUserStorageLimitRadioButtonLabel: string;
  afeUiSettingEnabled: boolean;
  derivedPermissionsLearnMoreHelpLink: string;
  metadataSettingEnabled: boolean;
  catalogObfuscationLearnMoreHelpLink: string;
  showUVWarning: boolean;
  userVisbilityWarning: string;
  subscriptionsEnabledOnSite: boolean;
  isCustomScheduleSubscriptionForSiteEnabled: boolean;
  isSelfServiceScheduleEnabled: boolean;
  isUserVisibilityLimited: boolean;
  showPublicCollectionsDisabledWarning: boolean;
  timeZoneSettingsLearnMoreHref: string;
  isNamedSharingWithUNPEnabled: boolean;
}

export class SiteSettingsForSiteAdminFormCtrl {

  // master is being compared against this.scope.siteSettings which does not match the declared type at runtime
  private master: any;
  private server: SessionManagementService;

  public static $inject = [
    '$scope',
    '$translate',
    'SettingsFormService',
    '$timeout',
  ];

  constructor(
    private scope: ISiteSettingsForSiteAdminFormScope,
            $translate: ng.translate.ITranslateService,
            formService: SettingsFormService,
            $timeout: ng.ITimeoutService
  ) {
    this.server = getSessionManagementServiceInstance();
    this.scope.isSiteInviteNotificationUnavailable = this.isSiteInviteNotificationUnavailable(this.scope.siteAuthenticationSettings);
    //endregion
    let siteAdminSettings = (<ISiteAdminSettings> this.scope.siteSettings.siteAdminSettings);
    // Site Start Page.
    //region Migrated to StartPageSection.tsx
    this.scope.showSiteStartPageSetting = FeatureFlags.siteDefaultStartPage && (this.server.isSessionMultiSite() || this.server.isOnlineEnvironment());
    this.scope.siteStartPage = this.scope.siteSettings.siteAdminSettings ? siteAdminSettings.siteStartPageUri : '';
    this.scope.serverStartPage = this.getServerStartPage();
    this.scope.resetDefaultSiteStartPage = () => UserSettingsActions.confirmResetSiteDefaultStartPage(() => {
      this.scope.siteStartPage = '';
    });
    //endregion

    //region Ask Data properties. Migrated to AskDataSection.tsx
    this.scope.askDataDisabled = () =>
      (<ISiteAdminSettings>this.scope.siteSettings.siteAdminSettings).askDataMode === AskDataMode.DISABLED_BY_DEFAULT ||
      (<ISiteAdminSettings>this.scope.siteSettings.siteAdminSettings).askDataMode === AskDataMode.DISABLED_ALWAYS;
    //endregion

    //region AngularJS Form Stuff
    this.scope.sharedForm = formService;
    this.scope.localForm = {
        formController: this.scope.siteSettingsForSiteAdminForm,
        save: () => this.callServerToUpdateSiteSettings(),
        cancel: () => this.revertChanges(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.siteSettings),
        resetForm: () => this.revertChanges(),
        resetSettings: null,
      };
    //endregion

    //region probably not needed for migration
    this.initializeUnsetValues();
    this.master = angular.copy(this.scope.siteSettings);
    // display warning for user visibility and put a placeholder here.
    this.scope.userVisbilityWarning = Messages.userVisibilityLimitedError();
    this.scope.$watch('siteSettings.siteAdminSettings.userVisibility', () => {
      this.shouldShowUserVisibilityWarning();
    });

    this.scope.$watch('siteSettings.siteAdminSettings.publicCollectionsEnabled', () => {
      this.shouldShowPublicCollectionsDisabledWarning();
    });

    this.scope.$watch('siteSettings.siteAdminSettings', (newValue: any) => {
      this.scope.$broadcast('siteAdminSettings.change', newValue);
    }, true);

    this.scope.$watch('serverSettings', (newValue: any) => {
      this.scope.$broadcast('serverSettings.change', newValue);
    }, true);

    this.scope.$watch('siteSettings.siteAdminSettings.siteCommentingSettings.commentingEnabled', (value: boolean) => {
      if (siteAdminSettings.siteCommentingSettings) {
        siteAdminSettings.siteCommentingSettings.commentingEnabled = value;
      }
    });
    //endregion

    // Extract Encryption properties.
    this.scope.showEARSetting = this.server.isExtractEncryptionEnabledOmniSwitch();
    if (this.scope.showEARSetting) {
      // This value controls whether the encryption status shows up or not in the view and controls whether the API to get status is called or not in the controller.
      this.scope.showStatus = this.scope.showEncryptionStatus;
      this.scope.extractEncryptionStatusError = false;
      this.scope.activitySpinnerProps = {
        testId: 'extract-encryption-spinner',
        spinnerSize: ActivitySpinnerSize.Small
      };
      this.getEncryptionStats();
      this.scope.$watch('siteSettings.siteAdminSettings.extractEncryptionMode', () => {
        this.shouldShowExtractEncryptionWarning();
      });
    }
    //endregion

    //region Migrated to CrossDataBaseJoinsSection.tsx
    this.scope.afeUiSettingEnabled = this.server.isAfeUiSettingEnabled;
    //endregion

    //region Catalog properties. Migrated to AutomaticAccessSection.tsx
    this.scope.derivedPermissionsLearnMoreHelpLink = HelpService.getServerHelpUrlByCategory('dm-derived');
    this.scope.metadataSettingEnabled = this.server.catalogingOnSiteEnabled();
    //endregion

    //region Migrated to SensitiveLineageDataSection.tsx
    this.scope.catalogObfuscationLearnMoreHelpLink = HelpService.getServerHelpUrlByCategory('dm-obfuscation');
    //endregion

    //region Migrated to SiteTimeZoneSection.tsx
    this.scope.timeZoneSettingsLearnMoreHref = HelpService.getServerHelpUrlByCategory('tz-for-extracts');
    //endregion

    //region Migrated to NotificationsFlowRunsSection.tsx
    this.failureNotificationContent();
    //endregion

    //region unused
    // TODO: Placeholder until Story 893142 is figured out.
    const siteStorageQuotaBytes = 5000000000;
    this.populateSandboxLabels(siteStorageQuotaBytes);
    //endregion
  }

  //region not needed for migration
  private initializeUnsetValues() {
    if (this.scope.siteSettings && this.scope.siteSettings.siteAdminSettings) {
      let siteAdminSettings = (<ISiteAdminSettings> this.scope.siteSettings.siteAdminSettings);
      if (siteAdminSettings && siteAdminSettings.sitePersonalSpaceSettings && !siteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuotaSet) {
        siteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuota = null;
      }
      // The backend sends the subscriptionSettings object as part of the site settings if
      // subscriptions are enabled at the server level. We check for its existence and put its value
      // into a boolean up front because ng-model will "helpfully" create the subscriptionSettings
      // object later during the digest to set up the 2-way binding for its inner fields. (TFS: 616257)
      this.scope.subscriptionsEnabledOnSite = !!this.scope.siteSettings.subscriptionSettings;
      // SubscriptionSettings Adapter: Move the setting to where it should belong for React Settings
      if (FeatureFlags.settingsReactMigration2) {
        transformSubscriptionSettingsForScope(this.scope.siteSettings);
      }
      this.scope.isCustomScheduleSubscriptionForSiteEnabled = this.scope.subscriptionsEnabledOnSite
        ? this.scope.siteSettings.subscriptionSettings.customScheduleSubscriptionForSiteEnabled
        : false;
      this.scope.isUserVisibilityLimited = FeatureFlags.userVisibilityEnabled &&
                                              siteAdminSettings.userVisibility === UserVisibilityMode.LIMITED;
    }
    this.scope.isSelfServiceScheduleEnabled = this.server.isSelfServiceScheduleEnabled();
    this.scope.isNamedSharingWithUNPEnabled = FeatureFlags.sharingInAppNotificationsEnabled &&
      FeatureFlags.userNotificationSettingsEnabled;
  }
  //endregion

  //region Migrated to NotificationsFlowRunsSection.tsx
  public failureNotificationContent() {
    if (this.server.flowsOnSiteEnabled()) {
      this.scope.failureNotificationTitle = Messages.extractsOrFlowEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.extractsOrFlowEmailNotificationSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.extractsOrFlowEmailNotificationSettingEnable();
    } else {
      this.scope.failureNotificationTitle = Messages.extractsEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.extractsEmailNotificationSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.extractsEmailNotificationSettingEnable();
    }
  }
  //endregion

  //region Looks unused
  private bytesToGBConverter(bytes: number): string {
    return Math.floor(bytes / ByteSizeConversionFactor.ONE_GB).toFixed(0);
  }

  public populateSandboxLabels(siteStorageQuotaBytes: number) {
    const siteStorageQuotaStr = this.bytesToGBConverter(siteStorageQuotaBytes);
    this.scope.sandboxSiteStorageLimitRadioButtonLabel = Messages.sandboxSiteSettingStorageLimitSiteQuota({SITE_LIMIT: siteStorageQuotaStr});
    this.scope.sandboxUserStorageLimitRadioButtonLabel = Messages.sandboxSiteSettingStorageLimitCustomQuota({SITE_LIMIT: siteStorageQuotaStr});
  }
  //endregion

  //region Migrated to ExtractEncryptionSection.tsx
  public getExtractEncryptionStatusDescription(data: IGetExtractEncryptionStatsResult): string {
    if(data.counts.encrypting > 0 && data.counts.decrypting > 0) {
      return Messages.ear_Status_Encryption_Decryption_In_Progress();
    } else if(data.counts.encrypting > 0) {
      return Messages.ear_Status_Encryption_In_Progress();
    } else if(data.counts.decrypting > 0) {
      return Messages.ear_Status_Decryption_In_Progress();
    } else {
      return Messages.ear_Status_Encryption_Not_In_Progress();
    }
  }

  public shouldShowExtractEncryptionWarning() {
    const siteAdminSettings = this.scope.siteSettings.siteAdminSettings as ISiteAdminSettings;
    if (!this.scope.showStatus || this.master.siteAdminSettings.extractEncryptionMode === siteAdminSettings.extractEncryptionMode) {
      // no warning if status is hidden or option is not changed.
      this.scope.extractEncryptionWarning = '';
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.DISABLED) {
      this.scope.extractEncryptionWarning = Messages.ear_Disable_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED) {
      this.scope.extractEncryptionWarning = Messages.ear_Enforce_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENABLED &&
              this.master.siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.DISABLED) {
      this.scope.extractEncryptionWarning = Messages.ear_Disable_To_Enable_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENABLED &&
              this.master.siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED) {
      this.scope.extractEncryptionWarning = Messages.ear_Enforce_To_Enable_Warning();
    } else {
      this.scope.extractEncryptionWarning = '';
    }
  }
  //endregion


  //region Migrated to UserVisibilitySection.tsx
  public shouldShowUserVisibilityWarning(){
    const siteAdminSettings = this.scope.siteSettings.siteAdminSettings as ISiteAdminSettings;
    const preSetting = this.master.siteAdminSettings.userVisibility;
    const curSetting = siteAdminSettings.userVisibility;
    if (preSetting === curSetting) {
      this.scope.showUVWarning = false;
    } else if (curSetting === UserVisibilityMode.FULL) {
      this.scope.showUVWarning = true;
      this.scope.userVisbilityWarning = Messages.userVisibilityFullError();
    } else {
      this.scope.showUVWarning = true;
      if (FeatureFlags.collectionsEnabled) {
        // With feature flag removal, rename variable to userVisibilityLimitedError
        this.scope.userVisbilityWarning = Messages.collectionsUserVisibilityLimitedError();
      } else {
        this.scope.userVisbilityWarning = Messages.userVisibilityLimitedError();
      }
    }
  }
  //endregion

  //region Migrated to CollectionsSection.tsx
  public shouldShowPublicCollectionsDisabledWarning() {
    const siteAdminSettings = this.scope.siteSettings.siteAdminSettings as ISiteAdminSettings;
    const preSetting = this.master.siteAdminSettings.publicCollectionsEnabled;
    const curSetting = siteAdminSettings.publicCollectionsEnabled;
    if (preSetting === curSetting) {
      this.scope.showPublicCollectionsDisabledWarning = false;
    } else {
      this.scope.showPublicCollectionsDisabledWarning = !curSetting;
    }
  }
  //endregion

  //region Migrated to ExtractEncryptionSection.tsx
  public getEncryptionStats() {
    // Show encryption status
    if (this.scope.showStatus) {
      this.pollEncryptionStats();
      // When this compnent is destroyed we want the polling to stop.
      this.scope.$on('$destroy', () => {
        ExtractEncryptionService.cancelExtractEncryptionStatsPolling();
      });
    }
  }

  // start polling for extract encryption stats
  // This method should never be called directly and only through getEncryptionStats()
  private pollEncryptionStats() {
    const site: ISiteSettingsForSiteAdmin = angular.copy(this.scope.siteSettings);
    const siteId = (site as any).id || site.siteId;

    const notifyCallback = (result: IGetExtractEncryptionStatsResult) => {
      this.UpdateEncryptionStats(result);
    };

    ExtractEncryptionService.pollExtractEncryptionStats(siteId, notifyCallback).then(
      (result: IGetExtractEncryptionStatsResult) => {
        //completed
        this.UpdateEncryptionStats(result);
      },
      () => {
        //error
        this.scope.extractEncryptionStatusError = true;
      }
    );
  }

  private isExtractEncryptionStatsValid(data: IGetExtractEncryptionStatsResult): boolean {
    if(data && data.counts) {
      return true;
    }
    // This failed due to invalid data we want to cancel the polling
    ExtractEncryptionService.cancelExtractEncryptionStatsPolling();
    this.scope.extractEncryptionStatusError = true;
    return false;
  }

  //Update scope variables with the extract encryption stats
  public UpdateEncryptionStats(data: IGetExtractEncryptionStatsResult) {
    // Check validity of the api response
    if (this.isExtractEncryptionStatsValid(data)) {
      this.scope.extractEncryptionStats = {
        total: data.counts.decrypting + data.counts.encrypting + data.counts.encrypted + data.counts.unencrypted,
        unencrypted: data.counts.encrypting + data.counts.unencrypted,
        encrypted: data.counts.decrypting + data.counts.encrypted,
        encrypting: data.counts.encrypting,
        decrypting: data.counts.decrypting,
        inProgress: data.counts.encrypting > 0 || data.counts.decrypting > 0
      };
      this.scope.extractEncryptionStatusDescription = this.getExtractEncryptionStatusDescription(data);
    }
  }
  //endregion

  //region Handled by Formik
  private revertChanges(): void {
    this.scope.siteSettings = angular.copy(this.master);
    // clear the warning when the changes are reverted
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.scope.localForm.formController.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }
  //endregion

  //region migrated to SiteInviteNotificationSection.tsx
  private isSiteInviteNotificationUnavailable(settings: ISiteAuthenticationSettings): boolean {
    // If the site isn't using SSO auth, the site invite feature is unavailable
    return !SiteAuthenticationSettingsService.isAuthenticationSSO(settings);
  }
  //endregion

  //region Migrated to ReactSiteSettingsForSiteAdminSubFormik.tsx
  // used by EditSiteAction and in the Site Settings page
  private callServerToUpdateSiteSettings(): ng.IPromise<any> {
    const site: ISiteSettingsForSiteAdmin = angular.copy(this.scope.siteSettings);
    let siteAdminSettings: ISiteAdminSettings = site.siteAdminSettings as ISiteAdminSettings; //typechecking union type

    // Transforms the setting object back to the original state before it's sent to the API
    transformSubscriptionSettingsForApi(siteAdminSettings);

    const siteId = (site as any).id || site.siteId; //todo - clean up entry paths to here so it is always the same
    this.scope.localForm.busy = true;
    let errors: IError[] = [];
    return Sites.instance.updateSiteSettingsForSiteAdmin(siteId, siteAdminSettings, site.subscriptionSettings)
      .then((result: IResult) => {
        if (result.errors && result.errors.length) {
          errors = errors.concat(result.errors);
          this.scope.sharedForm.state.updateFailure(errors, null);
        } else {
          this.scope.isCustomScheduleSubscriptionForSiteEnabled =
            this.scope.siteSettings.subscriptionSettings
            ? this.scope.siteSettings.subscriptionSettings.customScheduleSubscriptionForSiteEnabled
            : false;
          this.updateSuccess(site);
        }
      })
      .finally(() => {
        this.scope.localForm.busy = false;
        // This is to check the encryption status after the setting is saved.
        this.getEncryptionStats();
        // As the setting is saved we need to clear the warning.
        this.scope.extractEncryptionWarning = '';
        this.scope.showUVWarning = false;
        this.scope.showPublicCollectionsDisabledWarning = false;
      });
  }

  // Success/Error Handlers
  private updateSuccess(site: ISiteSettingsForSiteAdmin): void {
    if (FeatureFlags.userVisibilityEnabled) {
      this.scope.isUserVisibilityLimited = (site.siteAdminSettings as ISiteAdminSettings).userVisibility === UserVisibilityMode.LIMITED;
      if (this.scope.isUserVisibilityLimited) {
        this.clearSettingsForUserVisibilityLimited();
      }
    }

    // Only need to update current view and state if we're in server or site settings
    if (!NavLocationSelectors.inSettingsLocation(getAppStore().getState())) {
      return;
    }

    // Trigger update of session info state as settings update may have changed it.
    this.scope.server.refreshSessionInfo();

    const siteAdminSettings = site.siteAdminSettings as ISiteAdminSettings;
    const masterSiteAdminSettings = this.master.siteAdminSettings as ISiteAdminSettings;
    // send site setting for site admin telemetry events
    if(site && this.checkSiteAdminSettingsNotNull(siteAdminSettings) && this.master && this.checkSiteAdminSettingsNotNull(masterSiteAdminSettings)) {
      Telemetry.sendEvent<SiteSettingSiteAdminTelemetryEvent>(buildSiteSettingSiteAdminTelemetryEvent(site, this.master, getSiteSettingUUID(true)));
    }
    // This happens when getSessionInfo is updated (used for userVisibility)
    transformSubscriptionSettingsForScope(site);
    this.master = angular.copy(site);
  }

  private checkSiteAdminSettingsNotNull(siteAdminSettings: ISiteAdminSettings) {
    return siteAdminSettings && siteAdminSettings.siteNamedSharingSettings
      && siteAdminSettings.sitePersonalSpaceSettings && siteAdminSettings.askDataMode && siteAdminSettings.userVisibility;
  }
  //endregion

  //region migrated into corresponding settings sections to use the user visibility value to reset their checkboxes
  private clearSettingsForUserVisibilityLimited() {
    this.scope.siteSettings.siteAdminSettings = {
      ...this.scope.siteSettings.siteAdminSettings,
      siteCommentingSettings: {
        commentingEnabled: false,
        commentingMentionsEnabled: false
      },
      requestAccessEnabled: false,
      dataAlertsEnabled: false,
      vizRecUsernameVisibilityEnabled: false,
      siteNamedSharingSettings: {
        namedSharingEnabled: false
      }
    };
  }
  //endregion

  private getServerStartPage(): string {
    if (!this.scope.serverSettings) {
      return '/';
    }

    return this.scope.serverSettings.defaultStartPage || '/';
  }
}

Module.instance().controller('SiteSettingsForSiteAdminFormCtrl', SiteSettingsForSiteAdminFormCtrl);
